import React, { useState, useEffect } from 'react';
import { X, Upload, Save, Key, Mail, Eye, EyeOff, Check, AlertCircle } from 'lucide-react';
import { API_URL } from '../config/api.ts';

interface ProfileModalProps {
    isOpen: boolean;
    onClose: () => void;
    userData: any;
    onUpdateProfile: (data: any) => Promise<void>;
}

const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, onClose, userData, onUpdateProfile }) => {
    const [formData, setFormData] = useState({
        username: '',
        gender: 'Non Binary',
        birthday: '',
        bio: '',
        avatar: ''
    });
    const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    // Password setup states
    const [showPasswordSection, setShowPasswordSection] = useState(false);
    const [passwordData, setPasswordData] = useState({
        email: '',
        currentPassword: '',
        password: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');
    const [savingPassword, setSavingPassword] = useState(false);

    // Password validation
    const passwordChecks = {
        length: passwordData.password.length >= 8,
        uppercase: /[A-Z]/.test(passwordData.password),
        lowercase: /[a-z]/.test(passwordData.password),
        number: /[0-9]/.test(passwordData.password),
        match: passwordData.password === passwordData.confirmPassword && passwordData.password.length > 0
    };

    const isPasswordValid = Object.values(passwordChecks).every(Boolean);

    useEffect(() => {
        console.log('🔍 ProfileModal useEffect - isOpen:', isOpen, 'userData:', userData);
        console.log('🔑 OAuth info - isOAuthUser:', userData?.isOAuthUser, 'hasPassword:', userData?.hasPassword);
        if (isOpen && userData) {
            console.log('📝 Setting form data:', {
                username: userData.username,
                avatar: userData.avatar ? userData.avatar.substring(0, 50) + '...' : null
            });
            setFormData({
                username: userData.username || '',
                gender: userData.gender || 'Non Binary',
                birthday: userData.birthday || '',
                bio: userData.bio || '',
                avatar: userData.avatar || ''
            });
            setAvatarPreview(userData.avatar || null);
            // Pre-fill email if user has one
            if (userData.email) {
                setPasswordData(prev => ({ ...prev, email: userData.email }));
            }
        }
    }, [isOpen, userData]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({ ...prev, [name]: value }));
        setPasswordError('');
        setPasswordSuccess('');
    };

    const handleAvatarClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) { // 5MB limit
                alert('File is too large. Max size is 5MB.');
                return;
            }

            // Show preview immediately
            const reader = new FileReader();
            reader.onloadend = async () => {
                const base64String = reader.result as string;
                setAvatarPreview(base64String);

                // Upload to Cloudinary
                setLoading(true);
                try {
                    const response = await fetch(`${API_URL}/upload/user-avatar`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({ imageData: base64String }),
                        credentials: 'include'
                    });
                    const data = await response.json();

                    if (data.success) {
                        // Update form with Cloudinary URL
                        setFormData(prev => ({ ...prev, avatar: data.url }));
                        setAvatarPreview(data.url);
                        console.log('✅ Avatar uploaded to Cloudinary:', data.url);
                    } else {
                        alert(data.message || 'Error uploading avatar');
                        setAvatarPreview(userData.avatar || null);
                    }
                } catch (error) {
                    console.error('Error uploading avatar:', error);
                    alert('Error uploading avatar. Please try again.');
                    setAvatarPreview(userData.avatar || null);
                } finally {
                    setLoading(false);
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            // Send the full form data including the avatar URL
            await onUpdateProfile(formData);
            onClose();
        } catch (error) {
            console.error('Error updating profile:', error);
            alert('Error updating profile. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSetPassword = async () => {
        if (!isPasswordValid) return;

        setSavingPassword(true);
        setPasswordError('');
        setPasswordSuccess('');

        const isChange = userData?.hasPassword;
        const endpoint = isChange ? `${API_URL}/user/change-password` : `${API_URL}/user/set-password`;

        const payload = isChange ? {
            currentPassword: passwordData.currentPassword,
            newPassword: passwordData.password,
            confirmPassword: passwordData.confirmPassword
        } : passwordData;

        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                credentials: 'include'
            });
            const data = await response.json();

            if (data.success) {
                setPasswordSuccess(data.message);
                // Clear fields after short delay but keep success message
                setTimeout(() => {
                    setShowPasswordSection(false);
                    setPasswordData({ email: userData.email || '', currentPassword: '', password: '', confirmPassword: '' });
                }, 2000);
            } else {
                setPasswordError(data.message);
            }
        } catch (error) {
            setPasswordError('Error setting password. Please try again.');
        } finally {
            setSavingPassword(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(5px)'
        }}>
            <div style={{
                width: '600px',
                maxWidth: '90%',
                maxHeight: '90vh',
                backgroundColor: '#fff0f5', // Light pinkish background from screenshot
                borderRadius: '20px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}>
                {/* Header */}
                <div style={{
                    padding: '20px 24px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    borderBottom: '1px solid rgba(0,0,0,0.05)'
                }}>
                    <h2 style={{
                        fontSize: '24px',
                        fontWeight: '700',
                        color: '#1e293b',
                        margin: 0
                    }}>Edit Profile</h2>
                    <button
                        onClick={onClose}
                        style={{
                            position: 'absolute',
                            right: '20px',
                            background: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            padding: '8px',
                            color: '#64748b',
                            boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
                        }}
                    >
                        <X size={20} />
                    </button>
                    <div style={{
                        position: 'absolute',
                        bottom: '-10px',
                        fontSize: '12px',
                        color: '#64748b',
                        background: '#fff0f5', // Match modal bg to mask border
                        padding: '0 10px'
                    }}>
                        upload a 1:1 ratio image for best results. Images should be less than 10 MB.
                    </div>
                </div>

                {/* Content */}
                <div style={{
                    padding: '30px 40px',
                    overflowY: 'auto',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px'
                }}>
                    {/* Username */}
                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: '14px',
                            fontWeight: '700',
                            color: '#1e293b',
                            marginBottom: '4px'
                        }}>
                            User Name <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>
                            The name shown to other users when you create characters.
                        </div>
                        <input
                            type="text"
                            name="username"
                            value={formData.username}
                            onChange={handleChange}
                            style={{
                                width: '100%',
                                padding: '12px 16px',
                                borderRadius: '24px',
                                border: '1px solid #cbd5e1',
                                fontSize: '14px',
                                outline: 'none',
                                transition: 'border-color 0.2s'
                            }}
                        />
                        <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px', textAlign: 'right' }}>
                            {formData.username.length} characters
                        </div>
                    </div>

                    {/* Avatar, Change Avatar & Birthday - Same Line */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                        {/* Avatar Preview */}
                        <div style={{ position: 'relative' }}>
                            <div style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '50%',
                                overflow: 'hidden',
                                border: '3px solid white',
                                boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                            }}>
                                <img
                                    src={avatarPreview || "https://api.dicebear.com/7.x/avataaars/svg?seed=Felix"}
                                    alt="Avatar"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            </div>
                        </div>

                        <input
                            type="file"
                            accept="image/*"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                        />

                        {/* Change Avatar Button */}
                        <button
                            onClick={handleAvatarClick}
                            style={{
                                backgroundColor: '#ff6b9d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '24px',
                                padding: '10px 24px',
                                fontWeight: '600',
                                fontSize: '14px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                boxShadow: '0 4px 12px rgba(255, 107, 157, 0.3)'
                            }}>
                            <Upload size={18} />
                            Change Avatar
                        </button>

                        {/* Birthday */}
                        <div style={{ flex: 1, minWidth: '180px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: '700',
                                color: '#1e293b',
                                marginBottom: '8px'
                            }}>
                                Birthday <span style={{ fontWeight: '400', color: '#64748b' }}>(private)</span>
                            </label>
                            <input
                                type="date"
                                name="birthday"
                                value={formData.birthday}
                                onChange={handleChange}
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    borderRadius: '24px',
                                    border: '1px solid #cbd5e1',
                                    fontSize: '14px',
                                    outline: 'none',
                                    backgroundColor: 'white'
                                }}
                            />
                        </div>
                    </div>

                    {/* Bio */}
                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: '14px',
                            fontWeight: '700',
                            color: '#1e293b',
                            marginBottom: '8px'
                        }}>
                            Bio
                        </label>
                        <textarea
                            name="bio"
                            value={formData.bio}
                            onChange={handleChange}
                            placeholder="Introduce yourself"
                            rows={4}
                            style={{
                                width: '100%',
                                padding: '16px',
                                borderRadius: '16px',
                                border: '1px solid #cbd5e1',
                                fontSize: '14px',
                                outline: 'none',
                                resize: 'none',
                                fontFamily: 'inherit'
                            }}
                        />
                        <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                            {formData.bio.length} characters
                        </div>
                    </div>

                    {/* Email Login Setup - For OAuth users without password */}
                    {!showPasswordSection ? (
                        <button
                            onClick={() => setShowPasswordSection(true)}
                            style={{
                                background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '12px',
                                padding: '10px 20px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <Key size={16} />
                            {userData?.hasPassword ? 'Change Password' : 'Set Password'}
                        </button>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {/* Current Password - Only for changing */}
                            {userData?.hasPassword && (
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        name="currentPassword"
                                        placeholder="Current Password"
                                        value={passwordData.currentPassword}
                                        onChange={handlePasswordChange}
                                        style={{
                                            width: '100%',
                                            padding: '12px 40px 12px 16px',
                                            borderRadius: '12px',
                                            border: '1px solid #cbd5e1',
                                            fontSize: '14px',
                                            outline: 'none'
                                        }}
                                    />
                                </div>
                            )}

                            {/* Email - Only for setting first time */}
                            {!userData?.hasPassword && (
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="Email address"
                                    value={passwordData.email}
                                    onChange={handlePasswordChange}
                                    style={{
                                        padding: '12px 16px',
                                        borderRadius: '12px',
                                        border: '1px solid #cbd5e1',
                                        fontSize: '14px',
                                        outline: 'none'
                                    }}
                                />
                            )}

                            {/* Password */}
                            <div style={{ position: 'relative' }}>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    placeholder="Password"
                                    value={passwordData.password}
                                    onChange={handlePasswordChange}
                                    style={{
                                        width: '100%',
                                        padding: '12px 40px 12px 16px',
                                        borderRadius: '12px',
                                        border: '1px solid #cbd5e1',
                                        fontSize: '14px',
                                        outline: 'none'
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={{
                                        position: 'absolute',
                                        right: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: '#64748b'
                                    }}
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>

                            {/* Confirm Password */}
                            <input
                                type={showPassword ? 'text' : 'password'}
                                name="confirmPassword"
                                placeholder="Confirm password"
                                value={passwordData.confirmPassword}
                                onChange={handlePasswordChange}
                                style={{
                                    padding: '12px 16px',
                                    borderRadius: '12px',
                                    border: '1px solid #cbd5e1',
                                    fontSize: '14px',
                                    outline: 'none'
                                }}
                            />

                            {/* Password Requirements */}
                            <div style={{ fontSize: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {[
                                    { check: passwordChecks.length, label: '8+ chars' },
                                    { check: passwordChecks.uppercase, label: 'Uppercase' },
                                    { check: passwordChecks.lowercase, label: 'Lowercase' },
                                    { check: passwordChecks.number, label: 'Number' },
                                    { check: passwordChecks.match, label: 'Match' }
                                ].map(({ check, label }) => (
                                    <span
                                        key={label}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            color: check ? '#22c55e' : '#94a3b8'
                                        }}
                                    >
                                        {check ? <Check size={12} /> : <AlertCircle size={12} />}
                                        {label}
                                    </span>
                                ))}
                            </div>

                            {passwordError && (
                                <div style={{ color: '#ef4444', fontSize: '13px' }}>{passwordError}</div>
                            )}

                            {passwordSuccess && (
                                <div style={{ color: '#22c55e', fontSize: '13px' }}>{passwordSuccess}</div>
                            )}

                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                    onClick={() => setShowPasswordSection(false)}
                                    style={{
                                        padding: '10px 16px',
                                        borderRadius: '12px',
                                        border: '1px solid #e2e8f0',
                                        background: 'white',
                                        color: '#64748b',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSetPassword}
                                    disabled={!isPasswordValid || savingPassword}
                                    style={{
                                        padding: '10px 16px',
                                        borderRadius: '12px',
                                        border: 'none',
                                        background: isPasswordValid ? 'linear-gradient(135deg, #22c55e, #16a34a)' : '#e2e8f0',
                                        color: isPasswordValid ? 'white' : '#94a3b8',
                                        fontWeight: '600',
                                        cursor: isPasswordValid ? 'pointer' : 'not-allowed',
                                        opacity: savingPassword ? 0.7 : 1
                                    }}
                                >
                                    {savingPassword ? 'Saving...' : 'Save Password'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Status message for password users */}
                {userData?.hasPassword && !showPasswordSection && (
                    <div style={{
                        padding: '16px 20px',
                        backgroundColor: 'rgba(34, 197, 94, 0.1)',
                        borderRadius: '12px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Check size={20} style={{ color: '#22c55e' }} />
                            <span style={{ fontSize: '14px', color: '#1e293b' }}>
                                Email login is enabled with <strong>{userData.email}</strong>
                            </span>
                        </div>
                        <button
                            onClick={() => setShowPasswordSection(true)}
                            style={{
                                alignSelf: 'flex-start',
                                fontSize: '13px',
                                color: '#a855f7',
                                background: 'none',
                                border: 'none',
                                fontWeight: '600',
                                cursor: 'pointer',
                                textDecoration: 'underline'
                            }}
                        >
                            Change Password
                        </button>
                    </div>
                )}

                {/* Footer Actions */}
                <div style={{
                    padding: '20px 40px',
                    borderTop: '1px solid rgba(0,0,0,0.05)',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px'
                }}>
                    <button
                        onClick={onClose}
                        style={{
                            padding: '10px 24px',
                            borderRadius: '12px',
                            border: '1px solid #e2e8f0',
                            backgroundColor: 'white',
                            color: '#64748b',
                            fontWeight: '600',
                            cursor: 'pointer'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        style={{
                            padding: '10px 24px',
                            borderRadius: '12px',
                            border: 'none',
                            backgroundColor: '#2563eb',
                            color: 'white',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            opacity: loading ? 0.7 : 1
                        }}
                    >
                        {loading && <div className="spinner"></div>}
                        Save Changes
                    </button>
                </div>
            </div>
        </div >
    );
};

export default ProfileModal;
